import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { Paperclip } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

interface FileDropZoneProps {
  /** Called with the dropped files. Caller validates type/size/count. */
  onFiles: (files: FileList | File[]) => void;
  /** When true, drags are absorbed (no browser default) but no files attach. */
  disabled?: boolean;
  className?: string;
  overlayLabel?: string;
  children: ReactNode;
}

/**
 * Wraps an area so files can be dropped anywhere inside it (not just on the
 * composer). The depth counter tracks drag PRESENCE across nested children so
 * enter/leave stays symmetric; window listeners clear a stuck overlay when a
 * drag is released off-window. Extracted from the composer's original DnD.
 */
export const FileDropZone = ({
  onFiles,
  disabled = false,
  className,
  overlayLabel: overlayLabelProp,
  children,
}: FileDropZoneProps) => {
  const { t } = useTranslation(["common"]);
  const overlayLabel =
    overlayLabelProp ?? t("common:ai_assistant.drop_files_to_attach");
  const dragDepthRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const hasDraggedFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer.types).includes("Files");

  const clearDrag = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDragging(false);
  }, []);

  const handleDragEnter = (e: React.DragEvent) => {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault(); // always, so the browser never opens the file here
    dragDepthRef.current += 1;
    if (!disabled) setIsDragging(true);
  };
  const handleDragOver = (e: React.DragEvent) => {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault(); // required for drop to fire + suppresses browser default
    e.dataTransfer.dropEffect = disabled ? "none" : "copy";
  };
  const handleDragLeave = (e: React.DragEvent) => {
    if (!hasDraggedFiles(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault();
    clearDrag();
    if (disabled) return;
    const files = e.dataTransfer.files;
    if (files && files.length > 0) onFiles(files);
  };

  // Fallback reset: a drag dropped/aborted outside the wrapper (or released
  // off-window) may never deliver a final dragleave/drop, leaving the overlay
  // stuck. These window listeners always clear it.
  useEffect(() => {
    const onWindowDragLeave = (e: DragEvent) => {
      if (!e.relatedTarget) clearDrag(); // cursor left the document
    };
    window.addEventListener("drop", clearDrag);
    window.addEventListener("dragend", clearDrag);
    window.addEventListener("dragleave", onWindowDragLeave);
    return () => {
      window.removeEventListener("drop", clearDrag);
      window.removeEventListener("dragend", clearDrag);
      window.removeEventListener("dragleave", onWindowDragLeave);
    };
  }, [clearDrag]);

  return (
    <div
      className={cn("relative", className)}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && !disabled && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-sm border-2 border-dashed border-primary bg-primary-muted/85 backdrop-blur-xs">
          <span className="flex items-center gap-2 text-sm font-medium text-primary">
            <Paperclip className="size-4" />
            {overlayLabel}
          </span>
        </div>
      )}
    </div>
  );
};
